import React, { useCallback } from 'react';

import { ThreeDStockHeaderTeamTypes } from '../../ThreeDStockHeader.types';
import { IconsEnum } from '../../../../../../../assets/icons/types';
import { UUID } from '../../../../../../../types';

import { useLocalForageState } from '../../../../../../common/reactQuery/hooks/useLocalForageState';

import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';

import { StockTabs } from '../../../../../stockConstants';

import { ProductPath } from '../../../../../../products/ProductPath';
import { LifestylePath } from '../../../../../../lifestyles/LifestylePath';
import { MaterialPath } from '../../../../../../materials/MaterialPath';

import { ProductsPermissions } from '../../../../../../products/productsConstants';
import { LifestylesPermissions } from '../../../../../../lifestyles/lifestylesConstants';
import { MaterialsPermissions } from '../../../../../../materials/materialsConstants';
import { ProductCache } from '../../../../../../products/ProductCache';
import { LifestyleCache } from '../../../../../../lifestyles/LifestyleCache';
import { MaterialCache } from '../../../../../../materials/MaterialCache';

import {
  lifestylesKeys,
  materialsKeys,
  productsKeys
} from '../../../../../../../locales/keys';

const ThreeDStockHeaderAddButtonTitles = {
  [StockTabs.PRODUCTS]: productsKeys.addProduct,
  [StockTabs.LIFESTYLES]: lifestylesKeys.addLifestyle,
  [StockTabs.MATERIALS]: materialsKeys.addMaterial
};

const ThreeDStockHeaderAddButtonLinks = {
  [StockTabs.PRODUCTS]: ProductPath.create(),
  [StockTabs.LIFESTYLES]: LifestylePath.create(),
  [StockTabs.MATERIALS]: MaterialPath.create()
};

const ThreeDStockHeaderAddButtonPermissions = {
  [StockTabs.PRODUCTS]: ProductsPermissions.READ_NEW_PRODUCT_BUTTON,
  [StockTabs.LIFESTYLES]: LifestylesPermissions.READ_NEW_LIFESTYLE_BUTTON,
  [StockTabs.MATERIALS]: MaterialsPermissions.READ_NEW_MATERIAL_BUTTON
};

const ThreeDStockHeaderSelfAddButtonPermissions = {
  [StockTabs.PRODUCTS]:
    ProductsPermissions.READ_SELF_COMPANY_NEW_PRODUCT_BUTTON,
  [StockTabs.LIFESTYLES]:
    LifestylesPermissions.READ_SELF_COMPANY_NEW_LIFESTYLE_BUTTON,
  [StockTabs.MATERIALS]:
    MaterialsPermissions.READ_SELF_COMPANY_NEW_MATERIAL_BUTTON
};

const ThreeDStockHeaderOtherAddButtonPermissions = {
  [StockTabs.PRODUCTS]:
    ProductsPermissions.READ_OTHER_COMPANY_NEW_PRODUCT_BUTTON,
  [StockTabs.LIFESTYLES]:
    LifestylesPermissions.READ_OTHER_COMPANY_NEW_LIFESTYLE_BUTTON,
  [StockTabs.MATERIALS]:
    MaterialsPermissions.READ_OTHER_COMPANY_NEW_MATERIAL_BUTTON
};

const ThreeDStockHeaderOtherAddButtonTeamCacheKey = {
  [StockTabs.PRODUCTS]: ProductCache.createProductDataCacheKey(),
  [StockTabs.LIFESTYLES]: LifestyleCache.createLifestyleDataCacheKey(),
  [StockTabs.MATERIALS]: MaterialCache.createMaterialDataCacheKey()
};

function getAddButtonPermission(
  tab: StockTabs,
  isMyLibrary?: boolean,
  isLib?: boolean
) {
  if (isLib && !isMyLibrary) {
    return ThreeDStockHeaderOtherAddButtonPermissions[tab];
  }

  if (isMyLibrary) {
    return ThreeDStockHeaderSelfAddButtonPermissions[tab];
  }

  return ThreeDStockHeaderAddButtonPermissions[tab];
}

interface CreateItemData {
  teamIds: UUID[];
  nda: boolean;
}

interface ThreeDStockHeaderAddButtonProps {
  tab: StockTabs;
  isMyLibrary?: boolean;
  isLib?: boolean;
  team?: ThreeDStockHeaderTeamTypes;
}

function ThreeDStockHeaderAddButton({
  tab,
  isMyLibrary,
  isLib,
  team
}: ThreeDStockHeaderAddButtonProps) {
  const action = getAddButtonPermission(tab, isMyLibrary, isLib);
  const { setValue } = useLocalForageState<CreateItemData>(
    ThreeDStockHeaderOtherAddButtonTeamCacheKey[tab]
  );

  const handleClick = useCallback<() => void>(() => {
    if (team?.uuid) {
      setValue({
        teamIds: [team.uuid],
        nda: !!team.nda
      });
    }
  }, [setValue, team]);

  if (!tab) {
    return null;
  }

  return (
    <CheckPermissions action={action}>
      <div className="flex items-center">
        <NextLinkHelper
          className="py-2 pl-2 pr-2 sm:pr-4 sm:space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          dataGa="three-d-stock-products-new-product-link"
          href={ThreeDStockHeaderAddButtonLinks[tab]}
          onClick={handleClick}
        >
          <Icon className="h-6 w-6 p-0.5" icon={IconsEnum.PLUS} />
          <span className="sr-only sm:not-sr-only">
            <Translate id={ThreeDStockHeaderAddButtonTitles[tab]} />
          </span>
        </NextLinkHelper>
      </div>
    </CheckPermissions>
  );
}

export default ThreeDStockHeaderAddButton;
